import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { signalRService } from '../services/signalRService';
import { SIGNALR_EVENTS } from '../constants/signalREvents';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext';
import { getInbox, getMySubmissions } from '../api/approvalApi';
import { getSpaceId } from '../api/client';
import { TIFOXR_USER_ID_KEY, setNoApproverCallback } from '../api/apiConfig';
import NoApproverModal from '../components/approval/NoApproverModal';
import { SYSTEM_ROLES } from '../auth/permissions';

const ApprovalContext = createContext(null);

export const ApprovalProvider = ({ children }) => {
  const [inboxCount, setInboxCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [noApproverOpen, setNoApproverOpen] = useState(false);
  const [noApproverMessage, setNoApproverMessage] = useState('');

  useEffect(() => {
    setNoApproverCallback((msg) => { setNoApproverMessage(msg); setNoApproverOpen(true); });
    return () => setNoApproverCallback(null);
  }, []);
  const { toast } = useToast();
  const { activeSpace, isAuthenticated, signInError, user } = useAuth();
  const isGmsAccountant = user?.systemRole === SYSTEM_ROLES.GMS_ACCOUNTANT;
  const seenIds = useRef(new Set());
  const decidedByMe = useRef(new Set()); // approvalIds this user approved/rejected themselves
  const mySubmissionIds = useRef(new Set()); // approvalIds submitted by the current user

  const pushNotification = useCallback((item) => {
    // Deduplicate by id so inbox seeds on every 30s poll don't re-add or re-increment
    if (item.id) {
      if (seenIds.current.has(item.id)) return;
      seenIds.current.add(item.id);
    }
    setNotifications((prev) => [item, ...prev].slice(0, 20));
    setUnreadCount((c) => c + 1);
  }, []);

  const refreshInbox = useCallback(async () => {
    try {
      const items = await getInbox();
      setInboxCount(items.length);
      // Seed bell with existing pending items — catches approvals that arrived before page load
      items.forEach((item) => {
        // submittedAt has no timezone suffix — append Z so JS parses it as UTC, not local time
        const ts = item.submittedAt
          ? new Date(item.submittedAt.endsWith('Z') ? item.submittedAt : item.submittedAt + 'Z')
          : new Date();
        pushNotification({
          id: `inbox-${item.approvalId}`,
          approvalId: item.approvalId,
          type: 'requested',
          label: item.sectionLabel || 'Content',
          name: item.contentName || '',
          at: ts,
        });
      });
    } catch {
      // silently fail — space may not be selected yet
    }
  }, [pushNotification]);

  const markAllRead = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    seenIds.current.clear();
  }, []);

  const markOneRead = useCallback((index) => {
    setNotifications((prev) => {
      const next = prev.filter((_, i) => i !== index);
      setUnreadCount(Math.max(0, next.length));
      return next;
    });
  }, []);

  const recordDecision = useCallback((approvalId) => {
    if (approvalId) decidedByMe.current.add(String(approvalId));
  }, []);

  // Callers share one request while it is running (many Withdraw buttons can ask at the same moment).
  const refreshInFlight = useRef(null);
  const refreshMySubmissions = useCallback(() => {
    if (!refreshInFlight.current) {
      const request = (async () => {
        try {
          const items = await getMySubmissions();
          mySubmissionIds.current = new Set((items || []).map((i) => Number(i.approvalId)));
        } catch {
        }
      })();
      refreshInFlight.current = request.finally(() => { refreshInFlight.current = null; });
    }
    return refreshInFlight.current;
  }, []);

  const addMySubmission = useCallback((approvalId) => {
    if (approvalId != null) mySubmissionIds.current.add(Number(approvalId));
  }, []);

  const isMySubmission = useCallback((approvalId) => {
    if (approvalId == null) return false;
    return mySubmissionIds.current.has(Number(approvalId));
  }, []);

  useEffect(() => {
    // Discard notifications from the previous space before reseeding from the new one.
    // Must run before the spaceId guard so admin/entity switches also clear the bell.
    setNotifications([]);
    setUnreadCount(0);
    seenIds.current.clear();
    mySubmissionIds.current = new Set();
    refreshInFlight.current = null;   // a request still running for the previous space must not be reused


    // signInError catches a dead session isAuthenticated/getSpaceId() miss; SignalR bypasses axios.
    if (!isAuthenticated || signInError || !getSpaceId() || isGmsAccountant) return;

    let cancelled = false;

    const handleRequested = (payload) => {
      refreshInbox(); // also seeds bell with inbox items (including this new one)
      const name = payload.contentName || '';
      const label = payload.sectionLabel || 'Content';
      const approvalId = payload.approvalId;
      // Use inbox- prefix so it deduplicates against the seed from refreshInbox
      pushNotification({ id: approvalId ? `inbox-${approvalId}` : undefined, approvalId, type: 'requested', label, name, at: new Date() });
      toast.info(`${label} needs your approval${name ? ` — ${name}` : ''}`);
    };

    const handleDecision = (payload) => {
      const decision = payload.decision || '';
      const label = payload.sectionLabel || 'Content';
      const name = payload.contentName || '';
      const reason = payload.reason || '';
      const approvalId = String(payload.approvalId || '');

      // Clear seenId so that a resubmit of the same content (same approvalId
      // via SP UPSERT) is treated as a fresh notification, not a duplicate.
      if (approvalId) seenIds.current.delete(`inbox-${approvalId}`);

      const submitterId = payload.submitterUserId || '';
      const currentUserId = localStorage.getItem(TIFOXR_USER_ID_KEY) || '';
      const isSubmitter = submitterId && currentUserId &&
        submitterId.toLowerCase() === currentUserId.toLowerCase();

      // If this user was the one who clicked Approve/Reject, refresh their inbox
      // so the count drops immediately. Then skip the outcome toast UNLESS they are
      // also the submitter (demo_user self-approval) — in that case they still need
      // to see "Your content was approved/rejected".
      if (approvalId && decidedByMe.current.has(approvalId)) {
        refreshInbox();
        if (!isSubmitter) return;
      }

      // For all other cases, only the submitter sees the outcome toast.
      if (!isSubmitter) return;

      pushNotification({ approvalId: payload.approvalId, type: decision === 'approved' ? 'approved' : 'rejected', label, name, reason, at: new Date() });

      if (decision === 'approved') {
        // `reason` is empty for an approval unless the server has a note (e.g. run dates that passed while it waited).
        toast.success(`Your ${label} "${name}" was approved!${reason ? ` ${reason}` : ''}`);
      } else {
        toast.error(
          `Your ${label} "${name}" was rejected${reason ? `: ${reason}` : '.'}`,
          8000
        );
      }
    };

    const handleWithdrawn = () => {
      refreshInbox();
      refreshMySubmissions();
    };

    // Register listeners before starting so they are ready when applyListeners() runs post-connect
    signalRService.on(SIGNALR_EVENTS.APPROVAL_REQUESTED, handleRequested);
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);

    signalRService.start().catch(console.error);

    if (!cancelled) {
      refreshInbox();
      refreshMySubmissions();
    }

    return () => {
      cancelled = true;
      signalRService.off(SIGNALR_EVENTS.APPROVAL_REQUESTED, handleRequested);
      signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
      signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
      signalRService.stop().catch(console.error);
    };
  }, [activeSpace?.spaceId, isAuthenticated, signInError, isGmsAccountant, refreshInbox, pushNotification, toast, refreshMySubmissions]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ApprovalContext.Provider value={{ inboxCount, setInboxCount, refreshInbox, notifications, unreadCount, markAllRead, markOneRead, recordDecision, addMySubmission, isMySubmission, refreshMySubmissions }}>
      {children}
      <NoApproverModal open={noApproverOpen} onClose={() => setNoApproverOpen(false)} message={noApproverMessage} />
    </ApprovalContext.Provider>
  );
};

export const useApproval = () => {
  const ctx = useContext(ApprovalContext);
  if (!ctx) throw new Error('useApproval must be used inside <ApprovalProvider>');
  return ctx;
};
